


import { useEffect, useRef, useState } from "react";

const ITEM_HEIGHT = 34;
const VISIBLE_ITEMS = 5;
const CENTER_INDEX = 2;
const SCROLL_THRESHOLD = 40; // 👈 bigger = slower scroll

export const WheelScroller = ({
  values,
  value,
  onChange,
  formatter = (v) => v,
  faded = true,
}) => {
  const index = values.indexOf(value);
  const [currentIndex, setCurrentIndex] = useState(index);
  const containerRef = useRef(null);
  const wheelAccumulator = useRef(0);
  const touchStartY = useRef(null);

  useEffect(() => {
    setCurrentIndex(values.indexOf(value));
  }, [value, values]);

  const clamp = (i) =>
    Math.max(0, Math.min(values.length - 1, i));

  const move = (delta) => {
    const next = clamp(currentIndex + delta);
    if (next !== currentIndex) {
      setCurrentIndex(next);
      onChange(values[next]);
    }
  };

  /* ===============================
     NON-PASSIVE WHEEL HANDLER
  =============================== */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e) => {
      e.preventDefault(); // ✅ now allowed
      wheelAccumulator.current += e.deltaY;

      if (Math.abs(wheelAccumulator.current) >= SCROLL_THRESHOLD) {
        move(wheelAccumulator.current > 0 ? 1 : -1);
        wheelAccumulator.current = 0;
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", onWheel);
    };
  }, [currentIndex, values]);

  /* ===============================
     TOUCH SUPPORT (SLOW & NATURAL)
  =============================== */
  const onTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const onTouchEnd = (e) => {
    if (touchStartY.current == null) return;

    const diff = touchStartY.current - e.changedTouches[0].clientY;

    if (Math.abs(diff) > 30) {
      move(diff > 0 ? 1 : -1);
    }

    touchStartY.current = null;
  };

  return (
    <div
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        height: ITEM_HEIGHT * VISIBLE_ITEMS,
        overflow: "hidden",
        textAlign: "center",
        fontFamily: "Montserrat",
        userSelect: "none",
        touchAction: "none", // ✅ stops page scroll on touch
      }}
    >
      <div
        style={{
          transform: `translateY(${
            (CENTER_INDEX - currentIndex) * ITEM_HEIGHT
          }px)`,
          transition: "transform 0.25s ease-out",
        }}
      >
        {values.map((v, i) => (
          <div
            key={v}
            style={{
              height: ITEM_HEIGHT,
              lineHeight: `${ITEM_HEIGHT}px`,
              fontSize: i === currentIndex ? 18 : 16,
              fontWeight: i === currentIndex ? 600 : 500,
              color:
                i === currentIndex
                  ? "#000"
                  : faded
                  ? "rgba(0,0,0,0.2)"
                  : "#000",
            }}
          >
            {formatter(v)}
          </div>
        ))}
      </div>
    </div>
  );
};
